import { Button } from '@/components/ui/button'
import { DocumentTypeTag, type DocumentData } from '@/data/entities'
import {
  createNewSOP,
  createProject,
  createReferenceDocument,
  deleteReferenceDocument,
  generateSignedUploadUrl,
  mutationKeys,
  processSOP,
  uploadFileToSignedUrl,
} from '@/data/mutations'
import { queryKeys, verifyDocumentUploadStatus } from '@/data/queries'
import { useUploadingStore } from '@/stores/useUploadingStore'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import type { User } from 'firebase/auth'
import { useCallback } from 'react'
import * as toast from '@/components/ui/toast'
import * as Sentry from '@sentry/react'
import { useUserSettingsStore } from '@/stores/useUserSettingsStore'

interface GenerateSOPButtonProps {
  currentProjectId: string
  newProjectName?: string
  currentTag: string
  currentCategory: string
  currentFolder?: string
  processingResolution: 'SD' | 'HD' | 'FHD' | 'Original'
  tenantId: string
  files: File[]
  referenceDocumentDescription: string
  closeDialog: () => void
  disabled?: boolean
}

interface GenerateSOPMutationProps {
  authState: User
  currentProjectId: string
  currentTag: string
  currentCategory: string
  currentFolder?: string
  processingResolution: 'SD' | 'HD' | 'FHD' | 'Original'
  tenantId: string
  files: File[]
  referenceDocumentDescription: string
}

export function GenerateSOPButton({
  currentProjectId,
  newProjectName,
  currentTag,
  currentCategory,
  currentFolder,
  processingResolution = 'SD',
  files,
  tenantId,
  referenceDocumentDescription = 'Reference document',
  closeDialog,
  disabled,
}: GenerateSOPButtonProps) {
  const {
    uploading,
    setUploading,
    initializeFileStatuses,
    updateFileStatus,
    abortController,
  } = useUploadingStore()
  const { authState } = useRouteContext({ from: '/_authenticated' })
  const regenerateDocumentTitle = useUserSettingsStore(
    (state) => state.regenerateDocumentTitle,
  )
  const queryClient = useQueryClient()

  const generateSOPMutation = useMutation({
    mutationKey: mutationKeys.documents.create(
      tenantId,
      currentProjectId || '',
    ),
    mutationFn: async ({
      authState,
      currentProjectId,
      currentTag = DocumentTypeTag.SOP,
      currentCategory,
      currentFolder,
      processingResolution,
      tenantId,
      files,
      referenceDocumentDescription,
    }: GenerateSOPMutationProps) => {
      let projectId = currentProjectId
      if (files.length === 0) {
        throw new Error('No files selected')
      }

      if (projectId === null) {
        throw new Error('No project selected')
      }

      if (projectId === 'create-new-project') {
        // Create a new default project for the user if the project list is empty
        const { id } = await createProject({
          authState,
          tenantId,
          projectName: newProjectName || 'Default Project',
        })
        projectId = id
      }

      const createUploadPromises = files.map(async (file) => {
        try {
          // Update status to uploading
          updateFileStatus(file.name, { status: 'uploading' })

          if (!currentProjectId) {
            throw new Error('No project ID found')
          }

          // Create a new reference document in the database and get the documentId
          const referenceDocument = await createReferenceDocument({
            authState,
            tenantId,
            projectId,
            data: {
              collection: 'reference-documents',
              metadata: {
                filename: file.name,
                size: file.size,
                mimeType: file.type,
              },
              description: referenceDocumentDescription, // TODO: handle optional description from the user
            },
          })

          // Get the upload url for the reference document from the documentId
          const { url: signedUrl } = await generateSignedUploadUrl({
            authState,
            tenantId,
            projectId,
            filename: file.name,
            contentType: file.type,
            referenceDocumentId: referenceDocument.id,
          })

          // Upload the file to the signed url
          await uploadFileToSignedUrl({
            signedUrl,
            file,
            onProgress: (progress) => {
              updateFileStatus(file.name, { progress })
            },
            onComplete: () => {
              updateFileStatus(file.name, {
                status: 'validating',
                progress: 100,
              })
            },
            onError: (error, progress) => {
              updateFileStatus(file.name, { status: 'error', progress })

              if (error.name === 'CanceledError') {
                toast.info(`Upload for ${file.name} was cancelled.`)
              } else {
                Sentry.captureException(error)
                toast.error(`Error during upload process for ${file.name}.`, {
                  description: error.message,
                })
              }
            },
            abortController: abortController,
          }).catch(async (error) => {
            // If the upload fails, delete the reference document database entry
            await deleteReferenceDocument({
              authState,
              tenantId,
              projectId,
              referenceDocumentId: referenceDocument.id,
            })
            throw error
          })

          // Validate the file
          const token = await authState.getIdToken()
          const verified = await verifyDocumentUploadStatus({
            token,
            tenantId,
            projectId,
            referenceDocumentId: referenceDocument.id,
          })

          if (!verified) {
            updateFileStatus(file.name, {
              status: 'error',
              progress: 0,
            })
            throw new Error('File upload not completed')
          }
          updateFileStatus(file.name, {
            status: 'uploaded',
            progress: 100,
          })
          toast.success(`File ${file.name} uploaded successfully!`)

          // Create a new SOP in the database with the reference documentId
          toast.info(`Creating new SOP for ${file.name}...`)
          const { id } = await createNewSOP({
            authState,
            tenantId,
            projectId,
            referenceDocumentIds: [referenceDocument.id],
            title: file.name,
            tag: currentTag,
            category: currentCategory,
            processingResolution,
            folder: currentFolder,
          })

          // Create optimistic documents for each file
          const optimisticDocument: DocumentData = {
            id,
            title: file.name,
            creator: authState?.uid || '',
            status: 'PENDING',
            projectId,
            content: {
              sections: [],
              steps: [],
            },
            metadata: {
              type: currentTag,
              category: currentCategory,
            },
            currentVersion: 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }

          // Optimistically update all relevant queries
          queryClient.setQueryData(
            queryKeys.documents.all(tenantId),
            (old: DocumentData[] = []) => [optimisticDocument, ...old],
          )

          queryClient.setQueryData(
            queryKeys.documents.byProject(tenantId, currentProjectId),
            (old: DocumentData[] = []) => [optimisticDocument, ...old],
          )

          queryClient.setQueryData(
            queryKeys.documents.createdByMe,
            (old: DocumentData[] = []) => [optimisticDocument, ...old],
          )

          queryClient.setQueryData(
            queryKeys.documents.recent,
            (old: DocumentData[] = []) => [optimisticDocument, ...old],
          )

          queryClient.setQueryData(
            queryKeys.documents.latestUpdates,
            (old: DocumentData[] = []) => [optimisticDocument, ...old],
          )

          await processSOP({
            authState,
            tenantId,
            projectId,
            sopId: id,
            regenerateTitle: regenerateDocumentTitle,
          })
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error occurred'
          updateFileStatus(file.name, { status: 'error', progress: 0 })
          toast.error(`Error during upload process for ${file.name}.`, {
            description: errorMessage,
          })
        }
      })

      // Execute all uploads in parallel
      await Promise.all(createUploadPromises)
        .catch((error) => {
          console.error('Error in upload batch:', error)
        })
        .then(async () => {
          // Invalidate all relevant queries
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: queryKeys.documents.byProject(tenantId, projectId),
              refetchType: 'all',
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.documents.all(tenantId),
              refetchType: 'all',
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.documents.createdByMe,
              refetchType: 'all',
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.documents.recent,
              refetchType: 'all',
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.documents.latestUpdates,
              refetchType: 'all',
            }),
          ])
        })
    },
    onMutate: async ({ tenantId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.documents.all(tenantId),
      })

      // Snapshot the previous value
      const previousDocuments = queryClient.getQueryData(
        queryKeys.documents.all(tenantId),
      )

      return { previousDocuments }
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousDocuments) {
        queryClient.setQueryData(
          queryKeys.documents.all(variables.tenantId),
          context.previousDocuments,
        )
      }
      Sentry.captureException(err)
      toast.error('Failed to create SOP', {
        description:
          err instanceof Error ? err.message : 'Unknown error occurred',
      })
    },
    onSettled: (_, __, variables) => {
      // Always refetch after error or success to ensure we have the correct data
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.all(variables.tenantId),
      })
    },
  })

  const handleUpload = useCallback(() => {
    setUploading(true)
    initializeFileStatuses(files)

    generateSOPMutation.mutate({
      authState: authState!,
      currentProjectId: currentProjectId || '',
      currentTag,
      currentCategory,
      currentFolder,
      processingResolution,
      tenantId,
      files: files,
      referenceDocumentDescription: referenceDocumentDescription,
    })

    closeDialog()
  }, [
    setUploading,
    initializeFileStatuses,
    files,
    generateSOPMutation,
    authState,
    currentProjectId,
    currentTag,
    currentCategory,
    currentFolder,
    processingResolution,
    tenantId,
    referenceDocumentDescription,
    closeDialog,
  ])

  return (
    <Button disabled={uploading || disabled} onClick={handleUpload}>
      {uploading ? 'Uploading in progress...' : 'Upload'}
    </Button>
  )
}
